import { EnvIdentifier } from '../enums/env.enums';
import {
  FeatureFlagCriterionType,
  FeatureFlagEnvironment,
  FeatureFlagMachineMode,
} from '../enums/feature-flags.enums';
import { IAuthToken } from '../interfaces/i-auth-token';
import {
  IFeatureFlag,
  IFeatureFlagCriterion,
} from '../interfaces/feature-flags/i-feature-flag';
import { IFeatureFlagContext } from '../interfaces/feature-flags/i-feature-flag-context';

/**
 * Pure, shared (FE + BE + jobs) feature-flag utilities. No IO, no DB, no env
 * reads beyond the pure runtime-identifier mapping, so it is trivially testable
 * and safe to compile under the frontend's TypeScript 4.5.
 */
export class FeatureFlagHelper {
  /**
   * Collapses the many runtime identifiers into the three logical flag
   * environments. `sandbox` is intentionally excluded (returns `undefined` =>
   * no flags apply). Unknown identifiers also return `undefined` (fail closed).
   */
  static mapRuntimeToEnvironment(
    identifier: string | undefined
  ): FeatureFlagEnvironment | undefined {
    switch (identifier as EnvIdentifier) {
      case 'local':
      case 'dev':
      case 'ngrok':
        return FeatureFlagEnvironment.Dev;

      case 'staging':
        return FeatureFlagEnvironment.Staging;

      case 'prod':
      case 'prod-jp':
      case 'prod-kr':
      case 'alpha':
      case 'beta':
      case 'maintenance':
        return FeatureFlagEnvironment.Production;

      case 'sandbox':
      default:
        return undefined;
    }
  }

  /**
   * The logical environments a given deployment is allowed to manage in the
   * console. A deployment can only reach the flag stores it has DB access to:
   * dev is isolated (dev only), while staging / production share access to the
   * staging and production stores. `sandbox` / unknown identifiers manage
   * nothing (fail closed).
   */
  static getManageableEnvironments(
    identifier: string | undefined
  ): FeatureFlagEnvironment[] {
    switch (identifier as EnvIdentifier) {
      case 'local':
      case 'dev':
      case 'ngrok':
        return [FeatureFlagEnvironment.Dev];

      case 'staging':
      case 'prod':
      case 'prod-jp':
      case 'prod-kr':
      case 'alpha':
      case 'beta':
      case 'maintenance':
        return [
          FeatureFlagEnvironment.Staging,
          FeatureFlagEnvironment.Production,
        ];

      case 'sandbox':
      default:
        return [];
    }
  }

  /**
   * Whether `userID` is the owner (creator) of `flag`. Fail closed: a missing
   * `userID` or `createdBy` is never treated as ownership.
   */
  static isOwnedBy(
    flag: Pick<IFeatureFlag, 'createdBy'>,
    userID: string | undefined
  ): boolean {
    return !!userID && flag.createdBy?.userID === userID;
  }

  /**
   * Builds an evaluation context from an authenticated JWT. Centralizes the
   * `IAuthToken -> IFeatureFlagContext` mapping so controllers, jobs, and tests
   * all shape the context identically. Accepts `undefined` (e.g. an
   * unauthenticated request) and returns an all-empty context (fail closed).
   */
  static contextFromAuthToken(
    token: IAuthToken | undefined
  ): IFeatureFlagContext {
    return {
      userID: token?.userID,
      role: token?.role,
      teamID: token?.teamID,
      machineID: token?.machineID,
    };
  }

  /** evaluates a single criterion against a context; fail-closed on missing ctx */
  private static evaluateCriterion(
    criterion: IFeatureFlagCriterion,
    ctx: IFeatureFlagContext
  ): boolean {
    switch (criterion.type) {
      case FeatureFlagCriterionType.Role:
        return ctx.role !== undefined && criterion.roles.includes(ctx.role);

      case FeatureFlagCriterionType.User:
        return (
          ctx.userID !== undefined && criterion.userIds.includes(ctx.userID)
        );

      case FeatureFlagCriterionType.Team:
        return (
          ctx.teamID !== undefined && criterion.teamIds.includes(ctx.teamID)
        );

      case FeatureFlagCriterionType.Machine:
        // fail closed when the context has no machine at all
        if (ctx.machineID === undefined) {
          return false;
        }

        if (criterion.mode === FeatureFlagMachineMode.All) {
          return true;
        }

        return (criterion.machineIds ?? []).includes(ctx.machineID);

      default:
        return false;
    }
  }

  /**
   * Fail-closed evaluation. A missing / disabled flag => `false`. Criteria are
   * AND-composed; a flag with no criteria is a pure boolean toggle.
   */
  static evaluate(
    flag: IFeatureFlag | undefined,
    ctx: IFeatureFlagContext
  ): boolean {
    if (!flag || !flag.enabled) {
      return false;
    }

    return (flag.criteria ?? []).every((criterion) =>
      FeatureFlagHelper.evaluateCriterion(criterion, ctx)
    );
  }
}
